import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@cortex/web-design-system/components/ui/Avatar';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { CuttysarkLiveCandidate } from '@cortex/domain-product-innovation-lab-types';
import { useDeleteCuttysarkPhoto, useUploadCuttysarkPhoto } from '../../../hooks/useCuttysark.js';
import { candidatePhoto } from './cuttysark-helpers.js';
import { resizePhotoToJpeg } from './photo-resize.js';

interface CandidatePhotoUploadProps {
  sessionId: string;
  /** 保存済みの候補者。まだ保存していない行は undefined（保存してからアップロードする）。 */
  candidate: CuttysarkLiveCandidate | undefined;
}

/**
 * 運営画面の候補者の行に置く写真のアップロード・削除。ファイルを選ぶとブラウザで縮めてから送り、
 * 結果の写真をその場に出す。上限や形式の判定は API に任せ、失敗したら API のメッセージを出す。
 * 縮小(画像の読み込み)に失敗したときは API に届かないため、ここで案内を出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の運営が候補者の紹介用写真をアップロード・削除する
 * @graph-connects useUploadCuttysarkPhoto [calls] 写真のアップロード
 * @graph-connects useDeleteCuttysarkPhoto [calls] 写真の削除
 * @graph-connects resizePhotoToJpeg [calls] アップロード前の縮小
 */
export function CandidatePhotoUpload({
  sessionId,
  candidate,
}: CandidatePhotoUploadProps): ReactNode {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadCuttysarkPhoto(sessionId);
  const remove = useDeleteCuttysarkPhoto(sessionId);
  const [resizeFailed, setResizeFailed] = useState(false);

  if (!candidate) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('cuttysark.operate.photoSaveFirst')}
      </p>
    );
  }

  const busy = upload.isPending || remove.isPending;
  const errorMessage = resizeFailed
    ? t('cuttysark.operate.photoResizeFailed')
    : (upload.error ?? remove.error)?.message;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Avatar src={candidatePhoto(candidate) || undefined} name={candidate.name} size="md" />
      <input
        ref={input}
        type="file"
        accept="image/*"
        aria-label={t('cuttysark.operate.photoFile')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setResizeFailed(false);
          resizePhotoToJpeg(file).then(
            (photo) => {
              upload.mutate({ email: candidate.email, input: photo });
            },
            () => {
              setResizeFailed(true);
            },
          );
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={() => {
          input.current?.click();
        }}
      >
        {busy ? t('cuttysark.operate.photoUploading') : t('cuttysark.operate.uploadPhoto')}
      </Button>
      {candidate.photoVersion > 0 && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            remove.mutate(candidate.email);
          }}
        >
          {t('cuttysark.operate.deletePhoto')}
        </Button>
      )}
      {errorMessage && (
        <p className="w-full text-xs text-[var(--color-error)]" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
